import { G } from '@mobily/ts-belt';
import { Box, Typography } from '@mui/material';
import { ChipField, useNotify, useRecordContext, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';

import { FileInput, ShowSection } from '@sh4res/bo-common/components';
import { DownloadOperationsFileSample } from '@sh4res/bo-common/components/B2bCampaigns/DownloadSample';
import { AllocationsFileError } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/utils';
import { parseFileVacationDaysDistributor } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysFile';
import { B2bCampaignFormValues } from '@sh4res/bo-common/components/B2bCampaigns/types';
import { useWatchObject } from '@sh4res/bo-common/hooks';

const testId = 'BoExternalB2bCampaignVacationDaysAllocationsStep';

export const VacationDaysAllocationsStep = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const notify = useNotify();
  const record = useRecordContext<GetB2bCampaignOneResponse>();
  const { company_id: companyId, b2b_contract_ids: contractIds } =
    useWatchObject<B2bCampaignFormValues>()(['company_id', 'b2b_contract_ids']);

  const isEditable = record?.status === 'campaign_ending_grace_period';

  const onFileChange = async (file: File | null) => {
    if (!file) {
      setValue('allocations', null);
      return;
    }
    try {
      const data = await parseFileVacationDaysDistributor({ file });
      setValue('allocations', data, { shouldDirty: true });
    } catch (err) {
      const allocationsFileError = err as AllocationsFileError | Error;
      // eslint-disable-next-line no-console
      console.error(allocationsFileError);
      notify(
        allocationsFileError instanceof AllocationsFileError
          ? allocationsFileError.messageKey
          : 'resources.bo_common.b2b.campaigns.sections.allocations.errors.parsing_file',
        {
          type: 'error',
          messageArgs:
            allocationsFileError instanceof AllocationsFileError
              ? allocationsFileError.messageData
              : undefined,
        },
      );
      setValue('allocations_file', undefined);
    }
  };

  return (
    <Box>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.campaigns.sections.vacation_days_allocations.download_title"
        noCard={true}>
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.vacation_days_allocations.download_description',
          )}
        </Typography>
        {G.isNotNullable(companyId) && (
          <DownloadOperationsFileSample
            body={{
              kind: 'vacation_days_allocations_sample',
              company_id: companyId,
              contract_ids: contractIds,
              campaign_id: record?.id ?? null,
            }}
            label="resources.bo_common.b2b.campaigns.sections.vacation_days_allocations.download_link"
          />
        )}
      </ShowSection>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.campaigns.sections.vacation_days_allocations.upload_title"
        noCard={true}>
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.vacation_days_allocations.upload_description_part1',
          )}
        </Typography>
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.vacation_days_allocations.upload_description_part2',
          )}
        </Typography>

        {isEditable && (
          <FileInput
            data-testid={`${testId}_AllocationsFileInput`}
            source="allocations_file"
            accept=".xlsx,.xlsm"
            onChange={onFileChange}
            label="resources.bo_common.b2b.campaigns.fields.allocations_file">
            <ChipField data-testid={`${testId}_AllocationsFileTitleField`} source="title" />
          </FileInput>
        )}

        <Typography fontWeight={700}>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations_manual',
          )}
        </Typography>
      </ShowSection>
    </Box>
  );
};
